/**
 * UI-only state (not persisted): which concept is selected for editing in
 * the attribute panel, attribute-panel collapsed flag, etc.
 */
import { create } from "zustand";

/**
 * Modal popups triggered from the canvas during nesting interactions.
 * The store holds at most one pending prompt at a time and exposes a
 * Promise-based open/close API so the call site (drag handler) can
 * await the user's choice. Resolving with `null` means cancel.
 */
export type NestingPrompt =
  | {
      kind: "pick-relation-type";
      sourceConceptId: string;
      targetConceptId: string;
      relationTypeIds: string[];
      /**
       * Herschikken naar een andere container: de relaties die de OUDE
       * nesting uitdrukte (zie `relationsRepresentedByOldNesting`). De modal
       * toont dan een vinkje (standaard aan) om ze uit het model te
       * verwijderen. Afwezig = gewone eerste nesting.
       */
      oldNesting?: { parentConceptId: string; relationIds: string[] };
      /** `removeOld` = vinkje stond aan bij een bewuste keuze (type of
       *  "Geen relatie"). Esc / klik ernaast = annuleren → altijd false. */
      resolve: (chosen: string | null, removeOld: boolean) => void;
    }
  | {
      kind: "pick-hidden-relation";
      relationIds: string[];
      resolve: (chosen: string | null) => void;
    }
  | {
      kind: "pick-existing-or-new";
      // Feature B 2026-05-26: connect tussen 2 instances waar al
      // concept-niveau-relaties bestaan maar NIET op deze instance-paar.
      // Resolver returnt een bestaande relation-id (= clone die als
      // template voor een nieuwe relatie-instance op dit pair), de
      // string "new" (= matrix-default-auto-create), of null (= cancel).
      sourceConceptId: string;
      targetConceptId: string;
      existingRelationIds: string[];
      resolve: (chosen: string | "new" | null) => void;
    }
  | {
      // Duplicaat-detectie 2026-06-18: een net-gewijzigde/getekende relatie is
      // identiek aan één of meer bestaande (zelfde type + naam + richting).
      // Resolver returnt de winner-id (= samenvoegen IN die bestaande), "keep"
      // (= beide houden) of null (= cancel).
      kind: "merge-relation";
      subjectRelationId: string;
      candidateRelationIds: string[];
      resolve: (chosen: string | "keep" | null) => void;
    }
  | {
      // Concept-duplicaat-detectie 2026-06-19: een net-aangemaakt/hernoemd
      // concept is identiek aan één of meer bestaande (zelfde type + naam +
      // stereotypes). Resolver returnt de winner-id (= samenvoegen IN die
      // bestaande), "keep" (= beide houden) of null (= cancel). Named-only:
      // een naamloos concept triggert deze prompt niet.
      kind: "merge-concept";
      subjectConceptId: string;
      candidateConceptIds: string[];
      resolve: (chosen: string | "keep" | null) => void;
    };

/**
 * Eén regel in de groepeer-vraag: een object dat binnen de nieuwe omtrek valt.
 *
 * `relationTypeIds` is per object berekend — de matrix bepaalt wat er tússen
 * dít object en de groep mag, en het viewpoint snoeit dat verder. Twee objecten
 * in dezelfde omtrek kunnen dus verschillende lijstjes hebben, en zelfs bij
 * gelijke lijstjes wil de modelleur er per object van kunnen afwijken. Vandaar
 * één venster met een keuze per regel in plaats van één keuze voor de hele set.
 *
 * `vastgelegd` is gevuld wanneer er niets te kiezen valt: bij Sharp's
 * procesniveaus ligt de aggregatie vast, en bij een tekenobject, een
 * Sharp-attribuut of een C4-view hoort er helemaal geen relatie te komen. De
 * regel toont dan die reden in plaats van een keuzelijst.
 */
export interface GroupEncloseItem {
  conceptId: string;
  naam: string;
  typeLabel: string;
  relationTypeIds: string[];
  vastgelegd?: string;
}

/**
 * De vraag die verschijnt zodra een object door verschalen andere objecten
 * volledig omsluit: "hier een groep van maken?" — met per omsloten object de
 * keuze welke relatie erbij hoort.
 *
 * De resolver geeft een keuze per object terug (`null` = wel nesten, geen
 * relatie), of `null` voor het geheel wanneer de modelleur niet wil groeperen.
 */
export interface GroupEnclosePrompt {
  groepNaam: string;
  items: GroupEncloseItem[];
  resolve: (keuze: Record<string, string | null> | null) => void;
}

/**
 * Parent-delete-prompt (Fase 2): een concept dat de parent verwijderde maar
 * waar deze branch nog branch-local relaties aan had hangen. De reconcile bij
 * branch-switch levert deze; de modal biedt per item keep/accept.
 */
export interface ParentDeletePrompt {
  conceptId: string;
  label: { nl?: string; en?: string };
  typeId: string;
  relationIds: string[];
}

/**
 * Homoniem-link-suggestie (Fase 3): een branch-local concept dat (type + naam)
 * matcht met een parent/main-concept maar een losse logische identiteit heeft.
 * Gevuld door de reconcile bij branch-switch; de modal laat de modelleur per
 * paar kiezen: koppelen (origin_id-adoptie) of negeren.
 */
export interface HomonymPrompt {
  branchConceptId: string;
  parentConceptId: string;
  typeId: string;
  name: { nl?: string; en?: string };
}

export interface UiState {
  selectedConceptId: string | null;
  /**
   * When the user clicks a specific instance on the canvas, this holds
   * the canvas node id (= the viewState entry key) so the canvas can
   * highlight ONLY that copy. When the user picks a concept from the
   * Concept Explorer, this stays null so EVERY instance on the diagram
   * gets highlighted.
   */
  selectedInstanceId: string | null;
  selectedRelationId: string | null;
  /**
   * Multi-instance-refactor (2026-05-26): viewState entry-key van de
   * SPECIFIEKE relatie-instance die door de gebruiker is geselecteerd
   * via canvas-klik (parallel aan `selectedInstanceId` voor objecten).
   * AttributePanel gebruikt deze om instance-scope stereotype-attrs
   * te lezen/schrijven via `viewState[selectedRelationInstanceId]
   * .stereotypeAttributes`.
   *
   * Null wanneer:
   *  - Geen edge geselecteerd, OF
   *  - Edge gekozen via Explorer (Mode B — alleen domain-relation-id
   *    bekend, geen instance-context). In dat geval blijft
   *    `selectedRelationId` gevuld en valt AttributePanel terug op
   *    entity-scope-attrs alleen.
   */
  selectedRelationInstanceId: string | null;
  /**
   * Multi-select-uitbreidingen naast de single-select-velden hierboven.
   * Worden gevuld door panels die meerdere objecten tegelijk willen
   * markeren — bv. de GridPane via Shift/Cmd-klik op rijen. Canvas
   * spiegelt deze sets naar React Flow's `node.selected`/`edge.selected`
   * zodat álle geselecteerde objecten visueel oplichten, niet alleen
   * de single-select-anchor.
   *
   * Lege array (default) = geen multi-selectie actief; alleen single-
   * select-velden bepalen dan de canvas-highlight. Bevatten de set ook
   * de single-select-id zelf, dat is geen probleem (dedup-set in canvas).
   */
  selectedConceptIds: string[];
  /**
   * Het object waarvan de rest z'n eigenschap overneemt bij een
   * werkbalk-bewerking met een maatstaf: gelijke maat en uitlijnen.
   * Dat is het object dat je als **eerste** aanklikte; erbij-klikken laat
   * 'm staan.
   *
   * `null` betekent "geen maatstaf aangewezen" — dat is de situatie na een
   * sleepselectie, waar geen klikvolgorde bestaat. De werkbalk valt dan
   * terug op de omhullende rechthoek (uitlijnen) en op de grootste
   * (gelijke maat).
   *
   * Bewust een concept-id en niet een viewState-sleutel: de werkbalk
   * vertaalt 'm zelf naar het voorkomen op dít diagram, net als de rest
   * van de selectie.
   */
  selectionAnchorConceptId: string | null;
  selectedRelationIds: string[];
  /**
   * Multi-instance refactor (2026-05-26): per-edge selectie-set met
   * viewState-ENTRY-KEYS (UUID voor multi-instance, relation-id voor
   * canonical). Canvas-visuele edge-highlight gebruikt deze set om
   * exact die ene instance te markeren die de gebruiker selecteerde,
   * niet alle instances van dezelfde domain-relation.
   *
   * `selectedRelationIds` hierboven blijft RELATION-IDS bevatten (voor
   * AttributePanel, Relations-paneel, Toolbar bulk-actions die op de
   * domain-relation werken). Beide sets worden tegelijk gevuld door
   * de rubber-band-handler / onSelectionChange / onEdgeContextMenu.
   *
   * Lege array (default) = geen multi-selectie; visueel wordt dan
   * `selectedRelationId` (single-select) gebruikt.
   */
  selectedEdgeIds: string[];
  /**
   * Diagram die in de AttributePanel bewerkt wordt. Wordt gezet door
   * `selectDiagram` (vanuit de M6 Explorer's diagram-detail-paneel) en
   * gewist door `selectConcept` / `selectRelation` / `selectInstance`
   * zodat selecties elkaar wederzijds uitsluiten.
   */
  selectedDiagramId: string | null;
  /**
   * Selected bend-point on a relation's polyline. Set by clicking a
   * waypoint handle on the canvas; the canvas's Delete-keybind reads
   * this to remove the chosen point. Cleared when the user selects a
   * different relation, deselects, or removes the waypoint.
   */
  selectedWaypoint: { edgeId: string; index: number } | null;
  attributePanelOpen: boolean;
  /** Virtuele Glossary-view (Sharp Attribuuttypes). Werkt als pseudo-tab:
   *  `glossaryTabOpen` = de tab staat in de tab-bar; `glossaryFocused` = de
   *  Glossary is de actieve view (vervangt de Canvas). Zo blijft de tab
   *  staan als de modelleur naar een diagram wisselt en kan 'ie terugklikken.
   *  Geopend via de Explorer-node "Attribuuttypes" of het Repository-menu. */
  glossaryTabOpen: boolean;
  glossaryFocused: boolean;
  textPaneOpen: boolean;
  /** Persist the PlantUML pane open state across reloads. */
  textPanePinned: boolean;
  /** Concept-explorer / repository-browser panel visibility. */
  conceptExplorerOpen: boolean;
  /** Type-explorer modal (M8 §4): read-only overzicht van alle typen in de
   *  actieve branch. Modal/overlay, geen side-panel. Gelanceerd via het
   *  Repository-menu. */
  typeExplorerOpen: boolean;
  /** Overlay-regels-paneel (deterministische kleur/dim conditional formatting):
   *  side-panel gelanceerd via de canvas-toolbar. Per-diagram regelset. */
  overlayPanelOpen: boolean;
  /** Vereenvoudig-paneel (deterministische reductie → vereenvoudigde kopie):
   *  side-panel gelanceerd via de canvas-toolbar. */
  simplifyPanelOpen: boolean;
  /** "Toon in Explorer"-verzoek (canvas-contextmenu). De Explorer reageert:
   *  laag uitklappen, groep openen, scrollen + selecteren. `nonce` maakt
   *  herhaalde verzoeken op hetzelfde concept opnieuw actief. */
  explorerReveal: { conceptId: string; nonce: number } | null;
  /**
   * When true, the Concept Explorer's open state is remembered across
   * reloads (persisted to localStorage). The pin icon in the explorer
   * header toggles this. When false, the panel defaults to closed on
   * every fresh load.
   */
  conceptExplorerPinned: boolean;
  /** M6 Explorer (workpackage × branch × layer-tree) panel visibility. */
  explorerOpen: boolean;
  /** Persist M6 Explorer open state across reloads. */
  explorerPinned: boolean;
  /** Type-palette (left sidebar of element types) visibility. */
  typePaletteOpen: boolean;
  /** Persist the type-palette open state across reloads. */
  typePalettePinned: boolean;
  /** Diagrams panel (lists every diagram in the branch) visibility. */
  diagramsPanelOpen: boolean;
  /** Persist the diagrams-panel open state across reloads. */
  diagramsPanelPinned: boolean;
  /** Relations panel (lists every concept's outgoing or incoming relations). */
  relationsPanelOpen: boolean;
  /** Persist the relations-panel open state across reloads. */
  relationsPanelPinned: boolean;
  /**
   * Direction filter for the Relations panel:
   * - "from" → list relations that ORIGINATE at each concept (VAN).
   * - "to"   → list relations whose TARGET is each concept (NAAR).
   */
  relationsDirection: "from" | "to";
  /**
   * M6.5-W3b — actieve filter in de Base-Foldertree-paneel én Type-Palette.
   * `null` = geen filter (toon alles). Bij een gezette waarde tonen
   * FoldertreePanel en TypePalette alleen folders/groups van deze klasse.
   * Live alleen in Base-WP-modus; in andere WPs blijft 't ongebruikt.
   */
  baseFolderClassFilter: "archimate" | "sharp" | "bmc" | "c4" | null;
  /**
   * Currently visible nesting popup, if any. The drag handler in the
   * canvas opens it and the modal-component watches this slot to render.
   * `null` when no popup is open.
   */
  nestingPrompt: NestingPrompt | null;
  /**
   * Groepeer-vraag na het verschalen van een object over andere heen.
   * `null` wanneer er geen vraag openstaat.
   */
  groupEnclosePrompt: GroupEnclosePrompt | null;
  /**
   * Roadmap-matrix-setup (roadmap-tijdreizen.md §6.4): diagram-id van een net
   * aangemaakte/omgezette Roadmap-matrix die nog geconfigureerd moet worden
   * (Program + concept-type). Gevuld door changeDiagramViewKind/createDiagram;
   * de `RoadmapMatrixSetupModal` toont dan de aanmaak-popup. `null` = dicht.
   */
  roadmapMatrixSetupDiagramId: string | null;
  /**
   * Parent-delete-prompts (Fase 2): gevuld door de reconcile bij branch-switch.
   * Leeg = geen modal. De modal verwerkt ze per item via de resolve-endpoint.
   */
  parentDeletePrompts: ParentDeletePrompt[];
  /**
   * Homoniem-link-suggesties (Fase 3): gevuld door de reconcile bij
   * branch-switch. Leeg = geen modal. De modal verwerkt ze per paar.
   */
  homonymPrompts: HomonymPrompt[];
  /**
   * Language for the PlantUML preview, independent of the global UI
   * language. `null` means "follow the UI language". Set to "nl"/"en"
   * to lock the preview to a specific language.
   */
  textPaneLang: "nl" | "en" | null;
  /**
   * Onderwater venster — which "code-laag" view is currently shown:
   * `plantuml` (default, bidirectional), `mermaid` (bidirectional, M2),
   * or `svg` (read-only, derived from the canvas).
   */
  revealMode: RevealMode;
  /**
   * When true, the Onderwater venster splits its body horizontally into
   * editor (left) + live preview (right). Persisted in localStorage so
   * the user's last choice survives a refresh. Only applies in PlantUML
   * and Mermaid modes — SVG mode is itself the preview.
   */
  revealPreviewOpen: boolean;
  /**
   * Dev-toggle: kies tussen de bestaande tekst-editor (`"text"`, default)
   * en de nieuwe spreadsheet-georiënteerde Onderwater venster (`"grid"`).
   * Tijdelijk — wordt verwijderd zodra GridPane volwaardig de TextPane
   * vervangt. Niet persistent in localStorage; refresh = back to text.
   */
  revealPaneVariant: "text" | "grid";
  /**
   * PlantUML lijn-routing — keuze die de gebruiker maakt in de
   * Onderwater-header dropdown (alleen zichtbaar bij PlantUML+preview).
   * Persistent in localStorage onder `ae.plantumlLineType`.
   */
  plantumlLineType: PlantumlLineType;
  /**
   * PlantUML/Mermaid per-instance emit-modus (multi-instance-refactor
   * 2026-05-26). Default OFF: één Business_Object()-regel per concept-id
   * (logical view, ArchiMate-conform). ON: één regel per viewState-
   * entry met alias-suffix `_n` voor 2e+ instance; relaties verwijzen
   * naar de juiste instance-aliassen. Niet-Archi-conform; ondersteunt
   * roundtrip via parser.
   *
   * Persistent in localStorage onder `ae.plantumlPerInstance`.
   */
  plantumlPerInstance: boolean;

  /**
   * Format painter ("toverstaf"). Twee-staps interactie:
   *   1. Gebruiker selecteert bron-object + klikt op de wand → de
   *      visuele opmaak van dat object wordt vastgelegd in
   *      `formatPainter.captured`.
   *   2. Gebruiker selecteert doelobject(en) + klikt nogmaals op de
   *      wand → opmaak wordt overgenomen, state keert terug naar `null`.
   *
   * Capture is generiek genoeg om tussen nodes en edges te kruisen:
   * `strokeColor` mapt op `NodeStyle.strokeColor` ↔ `viewState.lineColor`,
   * `strokeStyle` op `NodeStyle.borderStyle` ↔ `viewState.lineStyle`.
   * Velden die op het doel niet bestaan worden overgeslagen.
   */
  formatPainter: { captured: CapturedFormat } | null;

  /**
   * Pick a concept by id (e.g. from the Concept Explorer). Clears
   * `selectedInstanceId` so all instances of the concept on the active
   * diagram get highlighted.
   */
  selectConcept: (id: string | null) => void;
  /**
   * Pick a specific canvas instance. Sets both `selectedInstanceId` (for
   * single-instance highlight) and `selectedConceptId` (so the Attribute
   * Panel still operates on the underlying concept).
   */
  selectInstance: (instanceId: string, conceptId: string) => void;
  selectRelation: (id: string | null) => void;
  /**
   * Vervang of wis de multi-select-sets. Panels die multi-selecteren
   * roepen deze direct aan met de complete nieuwe lijst — de canvas
   * spiegelt de wijziging naar `node.selected`/`edge.selected` via
   * een useEffect die op deze keys luistert.
   */
  setSelectedConceptIds: (ids: string[]) => void;
  setSelectionAnchorConceptId: (id: string | null) => void;
  setSelectedRelationIds: (ids: string[]) => void;
  setSelectedEdgeIds: (ids: string[]) => void;
  /**
   * Eénmalige "spring naar"-request voor de canvas-viewport. GridPane's
   * "Toon op canvas"-context-menu-item zet de id van het te tonen
   * concept/relation. Canvas luistert via useEffect op deze key en
   * roept ReactFlow's `setCenter` of `fitBounds` aan om het object
   * gecentreerd in beeld te brengen. `nonce` zorgt dat hetzelfde id
   * herhaald getriggerd kan worden (anders dedupliceert React's
   * dependency-vergelijking de actie weg).
   */
  viewportFocusRequest: { id: string; nonce: number } | null;
  requestViewportFocus: (id: string) => void;
  /**
   * Pick a diagram for editing in the AttributePanel. Clears concept /
   * relation / instance selection so only the diagram-form renders.
   */
  selectDiagram: (id: string | null) => void;
  selectWaypoint: (edgeId: string, index: number) => void;
  clearWaypointSelection: () => void;
  setAttributePanelOpen: (open: boolean) => void;
  /** Open + focus de Glossary (tab verschijnt en wordt actief). */
  openGlossary: () => void;
  /** Geef de Glossary focus (tab moet al/ wordt open). */
  focusGlossary: () => void;
  /** Haal focus weg (bv. bij wissel naar een diagram) — tab blijft staan. */
  blurGlossary: () => void;
  /** Sluit de tab volledig (kruisje) — verdwijnt uit de tab-bar. */
  closeGlossaryTab: () => void;
  setTextPaneOpen: (open: boolean) => void;
  setTextPanePinned: (pinned: boolean) => void;
  setConceptExplorerOpen: (open: boolean) => void;
  setConceptExplorerPinned: (pinned: boolean) => void;
  setTypeExplorerOpen: (open: boolean) => void;
  setOverlayPanelOpen: (open: boolean) => void;
  setSimplifyPanelOpen: (open: boolean) => void;
  /** Vraag de Explorer een concept te tonen + selecteren (bumpt nonce). */
  revealInExplorer: (conceptId: string) => void;
  setExplorerOpen: (open: boolean) => void;
  setExplorerPinned: (pinned: boolean) => void;
  setTypePaletteOpen: (open: boolean) => void;
  setTypePalettePinned: (pinned: boolean) => void;
  setDiagramsPanelOpen: (open: boolean) => void;
  setDiagramsPanelPinned: (pinned: boolean) => void;
  setRelationsPanelOpen: (open: boolean) => void;
  setRelationsPanelPinned: (pinned: boolean) => void;
  setRelationsDirection: (direction: "from" | "to") => void;
  setBaseFolderClassFilter: (filter: "archimate" | "sharp" | "bmc" | "c4" | null) => void;
  setRevealMode: (mode: RevealMode) => void;
  setRevealPreviewOpen: (open: boolean) => void;
  setRevealPaneVariant: (variant: "text" | "grid") => void;
  setPlantumlLineType: (lineType: PlantumlLineType) => void;
  setPlantumlPerInstance: (perInstance: boolean) => void;
  /** Wand-eerste-klik: bevriest de meegegeven opmaak. */
  armFormatPainter: (captured: CapturedFormat) => void;
  /** Wand-tweede-klik geeft state vrij; ook bruikbaar als annuleer-actie. */
  clearFormatPainter: () => void;
  /**
   * Promise-based open of a nesting popup. The drag handler awaits the
   * Promise; the modal component calls the resolver via `closeNestingPrompt`
   * when the user picks or cancels.
   */
  promptNestRelationType: (
    sourceConceptId: string,
    targetConceptId: string,
    relationTypeIds: string[],
    oldNesting?: { parentConceptId: string; relationIds: string[] },
  ) => Promise<{ chosen: string | null; removeOld: boolean }>;
  promptHiddenRelationPick: (relationIds: string[]) => Promise<string | null>;
  /**
   * Opent de groepeer-vraag en wacht op het antwoord. Zie
   * `GroupEnclosePrompt` voor wat de resolver teruggeeft.
   */
  promptGroupOnEnclose: (
    groepNaam: string,
    items: GroupEncloseItem[],
  ) => Promise<Record<string, string | null> | null>;
  resolveGroupEnclosePrompt: (keuze: Record<string, string | null> | null) => void;
  /** Feature B: popup wanneer concept-niveau relaties bestaan maar
   *  niet op de geklikte instance-paar. Resolver returnt een bestaande
   *  relation-id (= clone-template), "new" (= matrix-default auto-create),
   *  of null (= cancel). */
  promptExistingOrNewRelation: (
    sourceConceptId: string,
    targetConceptId: string,
    existingRelationIds: string[],
  ) => Promise<string | "new" | null>;
  /** Duplicaat-detectie: vraag of de net-gewijzigde relatie samengevoegd moet
   *  worden met een bestaande. Returnt winner-id | "keep" | null. */
  promptMergeRelations: (
    subjectRelationId: string,
    candidateRelationIds: string[],
  ) => Promise<string | "keep" | null>;
  /** Concept-duplicaat-detectie: vraag of het net-aangemaakte/hernoemde concept
   *  samengevoegd moet worden met een bestaande kloon. winner-id | "keep" | null. */
  promptMergeConcepts: (
    subjectConceptId: string,
    candidateConceptIds: string[],
  ) => Promise<string | "keep" | null>;
  /** Internal — used by the modal component to settle the active prompt. */
  /** `removeOld` alleen relevant voor `pick-relation-type` met `oldNesting`. */
  resolveNestingPrompt: (chosen: string | null, removeOld?: boolean) => void;
  setTextPaneLang: (lang: "nl" | "en" | null) => void;
  setParentDeletePrompts: (prompts: ParentDeletePrompt[]) => void;
  /** Roadmap-matrix-setup-popup openen (na aanmaak/omzetting) / sluiten (§6.4). */
  openRoadmapMatrixSetup: (diagramId: string) => void;
  closeRoadmapMatrixSetup: () => void;
  setHomonymPrompts: (prompts: HomonymPrompt[]) => void;
}

const PIN_KEY_EXPLORER = "ae.conceptExplorerPinned";
const PIN_KEY_M6_EXPLORER = "ae.m6ExplorerPinned";
const PIN_KEY_PALETTE = "ae.typePalettePinned";
const PIN_KEY_DIAGRAMS = "ae.diagramsPanelPinned";
const PIN_KEY_TEXTPANE = "ae.textPanePinned";
const PIN_KEY_RELATIONS = "ae.relationsPanelPinned";
const RELATIONS_DIRECTION_KEY = "ae.relationsDirection";
const REVEAL_MODE_KEY = "ae.revealMode";
const REVEAL_PREVIEW_KEY = "ae.revealPreviewOpen";
const PLANTUML_LINETYPE_KEY = "ae.plantumlLineType";
const PLANTUML_PER_INSTANCE_KEY = "ae.plantumlPerInstance";

function readPinnedFromStorage(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writePinnedToStorage(key: string, pinned: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (pinned) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {
    // Storage may be unavailable (private mode, quota); silently ignore.
  }
}

const initialExplorerPinned = readPinnedFromStorage(PIN_KEY_EXPLORER);
const initialM6ExplorerPinned = readPinnedFromStorage(PIN_KEY_M6_EXPLORER);
const initialPalettePinned = readPinnedFromStorage(PIN_KEY_PALETTE);
const initialDiagramsPinned = readPinnedFromStorage(PIN_KEY_DIAGRAMS);
const initialTextPanePinned = readPinnedFromStorage(PIN_KEY_TEXTPANE);
const initialRelationsPinned = readPinnedFromStorage(PIN_KEY_RELATIONS);

function readRelationsDirection(): "from" | "to" {
  if (typeof window === "undefined") return "from";
  try {
    const v = window.localStorage.getItem(RELATIONS_DIRECTION_KEY);
    return v === "to" ? "to" : "from";
  } catch {
    return "from";
  }
}

function writeRelationsDirection(direction: "from" | "to"): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RELATIONS_DIRECTION_KEY, direction);
  } catch {
    // ignore
  }
}

const initialRelationsDirection = readRelationsDirection();

/**
 * Onderwater venster (reveal pane) — three switchable modes for showing the
 * "code-laag" of the active diagram. Persisted to localStorage so the user's
 * last-chosen mode survives a refresh.
 */
/**
 * SVG-mode is verwijderd 2026-05-24 (gebruiker gebruikte 't niet).
 * Type beperkt tot PlantUML + Mermaid. localStorage-fallback in
 * readRevealMode mapt oude "svg"-waarden naar "plantuml".
 */
export type RevealMode = "plantuml" | "mermaid";

/**
 * PlantUML lijn-routing-keuze — bepaalt welke `skinparam linetype` in
 * de emitter-output komt. De gebruiker kiest dit in een dropdown in
 * de Onderwater-header.
 *   - `"ortho"`    → rechte hoeken (default, lijkt op canvas)
 *   - `"polyline"` → rechte segmenten, mogen schuin
 *   - `"default"`  → PlantUML's curved/spline routing
 */
export type PlantumlLineType = "default" | "polyline" | "ortho";

/**
 * Visuele opmaak die met de "toverstaf" wordt gekopieerd. Bewust een
 * neutrale tussenlaag (geen `NodeStyle` of edge-viewState rechtstreeks)
 * zodat dezelfde bag tussen nodes en edges over en weer kan: een
 * concept met dashed grijze rand kopiëren naar een edge geeft die edge
 * dezelfde stroke-kleur en dash-pattern.
 */
export interface CapturedFormat {
  // Stroke-eigenschappen — gemeenschappelijk tussen node-border en
  // edge-stroke (mappen 1-op-1).
  strokeColor?: string;
  strokeStyle?: "solid" | "dashed" | "dotted" | "none";
  // Node-only — toegepast op NodeStyle, overgeslagen op edges.
  fillColor?: string;
  fontColor?: string;
  fontSize?: number;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  // Edge-only — toegepast op viewState.labelBgColor, overgeslagen op nodes.
  labelBgColor?: string;
}

function readRevealMode(): RevealMode {
  if (typeof window === "undefined") return "plantuml";
  try {
    const v = window.localStorage.getItem(REVEAL_MODE_KEY);
    // Backward-compat: oude opgeslagen "svg"-waarde mapt naar "plantuml"
    // (SVG-mode is verwijderd).
    return v === "mermaid" ? v : "plantuml";
  } catch {
    return "plantuml";
  }
}

function writeRevealMode(mode: RevealMode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(REVEAL_MODE_KEY, mode);
  } catch {
    // ignore
  }
}

const initialRevealMode = readRevealMode();

function readRevealPreviewOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(REVEAL_PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

function writeRevealPreviewOpen(open: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (open) window.localStorage.setItem(REVEAL_PREVIEW_KEY, "1");
    else window.localStorage.removeItem(REVEAL_PREVIEW_KEY);
  } catch {
    // ignore
  }
}

const initialRevealPreviewOpen = readRevealPreviewOpen();

function readPlantumlLineType(): PlantumlLineType {
  if (typeof window === "undefined") return "ortho";
  try {
    const v = window.localStorage.getItem(PLANTUML_LINETYPE_KEY);
    if (v === "default" || v === "polyline" || v === "ortho") return v;
    return "ortho";
  } catch {
    return "ortho";
  }
}

function writePlantumlLineType(lineType: PlantumlLineType): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PLANTUML_LINETYPE_KEY, lineType);
  } catch {
    // ignore
  }
}

const initialPlantumlLineType = readPlantumlLineType();

function readPlantumlPerInstance(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PLANTUML_PER_INSTANCE_KEY) === "1";
  } catch {
    return false;
  }
}

function writePlantumlPerInstance(perInstance: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (perInstance) window.localStorage.setItem(PLANTUML_PER_INSTANCE_KEY, "1");
    else window.localStorage.removeItem(PLANTUML_PER_INSTANCE_KEY);
  } catch {
    // ignore
  }
}

const initialPlantumlPerInstance = readPlantumlPerInstance();

export const useUi = create<UiState>((set) => ({
  selectedConceptId: null,
  selectedInstanceId: null,
  selectedRelationId: null,
  selectedRelationInstanceId: null,
  selectedConceptIds: [],
  selectionAnchorConceptId: null,
  selectedRelationIds: [],
  selectedEdgeIds: [],
  selectedDiagramId: null,
  selectedWaypoint: null,
  // Default dicht — paneel opent alleen op expliciete dubbelklik op
  // canvas-node/edge of in Explorer-detail-rij.
  attributePanelOpen: false,
  glossaryTabOpen: false,
  glossaryFocused: false,
  // Pin → "stay open across reloads"; without a pin the PlantUML pane
  // collapses on refresh, mirroring the Concept Explorer / Type Palette /
  // Diagrams panel pin semantics.
  textPaneOpen: initialTextPanePinned,
  textPanePinned: initialTextPanePinned,
  // If pinned in a previous session, open the explorer immediately so the
  // user doesn't have to re-open it after every refresh.
  conceptExplorerOpen: initialExplorerPinned,
  conceptExplorerPinned: initialExplorerPinned,
  typeExplorerOpen: false,
  overlayPanelOpen: false,
  simplifyPanelOpen: false,
  explorerReveal: null,
  explorerOpen: initialM6ExplorerPinned,
  explorerPinned: initialM6ExplorerPinned,
  // Pin → "keep me open across reloads". Without a pin the palette
  // collapses on refresh just like the Concept Explorer.
  typePaletteOpen: initialPalettePinned,
  typePalettePinned: initialPalettePinned,
  diagramsPanelOpen: initialDiagramsPinned,
  diagramsPanelPinned: initialDiagramsPinned,
  relationsPanelOpen: initialRelationsPinned,
  relationsPanelPinned: initialRelationsPinned,
  relationsDirection: initialRelationsDirection,
  baseFolderClassFilter: null,
  nestingPrompt: null,
  groupEnclosePrompt: null,
  parentDeletePrompts: [],
  roadmapMatrixSetupDiagramId: null,
  homonymPrompts: [],
  textPaneLang: null,
  revealMode: initialRevealMode,
  revealPreviewOpen: initialRevealPreviewOpen,
  revealPaneVariant: "grid",
  plantumlLineType: initialPlantumlLineType,
  plantumlPerInstance: initialPlantumlPerInstance,
  formatPainter: null,

  // Select-acties openen NIET meer auto het AttributePanel — gebruiker
  // gaf 2026-05-07 aan dat het paneel "vaak in de weg zit". Het opent
  // nu alleen via expliciete actie: dubbelklik op canvas-node/edge,
  // of dubbelklik op een rij in het Explorer-detail-paneel.
  // `attributePanelOpen` blijft ongewijzigd in deze acties — als 't
  // open stond blijft 't open (volg-gedrag van branch-copy), als 't
  // dicht stond blijft 't dicht.
  // NB: alle select-acties wissen tegelijk de multi-sets — single-select
  // is altijd een atomaire actie ("alleen dit ene is geselecteerd").
  // Een caller die een single-click én een multi-set-update wil doen
  // (bv. GridPane's plain-click) zet beide expliciet: eerst selectConcept,
  // dan setSelectedConceptIds via z'n useEffect. React batches → één
  // re-render met beide gezet.
  selectConcept: (id) =>
    set((s) => ({
      selectedConceptId: id,
      selectedInstanceId: null,
      selectedRelationId: null,
      selectedRelationInstanceId: null,
      selectedConceptIds: [],
      selectionAnchorConceptId: null,
      selectedRelationIds: [],
      selectedEdgeIds: [],
      selectedDiagramId: null,
      selectedWaypoint: null,
      attributePanelOpen: s.attributePanelOpen,
    })),

  selectInstance: (instanceId, conceptId) =>
    set((s) => ({
      selectedConceptId: conceptId,
      selectedInstanceId: instanceId,
      selectedRelationId: null,
      selectedRelationInstanceId: null,
      selectedConceptIds: [],
      selectionAnchorConceptId: null,
      selectedRelationIds: [],
      selectedEdgeIds: [],
      selectedDiagramId: null,
      selectedWaypoint: null,
      attributePanelOpen: s.attributePanelOpen,
    })),

  selectRelation: (id) =>
    set((s) => ({
      selectedRelationId: id,
      selectedConceptId: null,
      selectedInstanceId: null,
      selectedConceptIds: [],
      selectionAnchorConceptId: null,
      selectedRelationIds: [],
      selectedEdgeIds: [],
      selectedDiagramId: null,
      selectedWaypoint: null,
      attributePanelOpen: s.attributePanelOpen,
    })),

  selectDiagram: (id) =>
    set((s) => ({
      selectedDiagramId: id,
      selectedConceptId: null,
      selectedInstanceId: null,
      selectedRelationId: null,
      selectedRelationInstanceId: null,
      selectedConceptIds: [],
      selectionAnchorConceptId: null,
      selectedRelationIds: [],
      selectedEdgeIds: [],
      selectedWaypoint: null,
      attributePanelOpen: s.attributePanelOpen,
    })),

  selectWaypoint: (edgeId, index) => set({ selectedWaypoint: { edgeId, index } }),
  clearWaypointSelection: () => set({ selectedWaypoint: null }),

  setAttributePanelOpen: (open) => set({ attributePanelOpen: open }),
  setTypeExplorerOpen: (open) => set({ typeExplorerOpen: open }),
  setOverlayPanelOpen: (open) => set({ overlayPanelOpen: open }),
  setSimplifyPanelOpen: (open) => set({ simplifyPanelOpen: open }),
  revealInExplorer: (conceptId) =>
    set((s) => ({ explorerReveal: { conceptId, nonce: (s.explorerReveal?.nonce ?? 0) + 1 } })),
  openGlossary: () => set({ glossaryTabOpen: true, glossaryFocused: true }),
  focusGlossary: () => set({ glossaryTabOpen: true, glossaryFocused: true }),
  blurGlossary: () => set({ glossaryFocused: false }),
  closeGlossaryTab: () => set({ glossaryTabOpen: false, glossaryFocused: false }),
  setTextPaneOpen: (open) => set({ textPaneOpen: open }),
  setTextPanePinned: (pinned) => {
    writePinnedToStorage(PIN_KEY_TEXTPANE, pinned);
    set((s) => ({
      textPanePinned: pinned,
      textPaneOpen: pinned ? true : s.textPaneOpen,
    }));
  },
  setConceptExplorerOpen: (open) => set({ conceptExplorerOpen: open }),
  setConceptExplorerPinned: (pinned) => {
    writePinnedToStorage(PIN_KEY_EXPLORER, pinned);
    // Pinning while the panel is closed snaps it open so the pin click is
    // visibly meaningful; unpinning leaves the current open state alone.
    set((s) => ({
      conceptExplorerPinned: pinned,
      conceptExplorerOpen: pinned ? true : s.conceptExplorerOpen,
    }));
  },
  setExplorerOpen: (open) => set({ explorerOpen: open }),
  setExplorerPinned: (pinned) => {
    writePinnedToStorage(PIN_KEY_M6_EXPLORER, pinned);
    set((s) => ({
      explorerPinned: pinned,
      explorerOpen: pinned ? true : s.explorerOpen,
    }));
  },
  setTypePaletteOpen: (open) => set({ typePaletteOpen: open }),
  setTypePalettePinned: (pinned) => {
    writePinnedToStorage(PIN_KEY_PALETTE, pinned);
    set((s) => ({
      typePalettePinned: pinned,
      typePaletteOpen: pinned ? true : s.typePaletteOpen,
    }));
  },
  setDiagramsPanelOpen: (open) => set({ diagramsPanelOpen: open }),
  setDiagramsPanelPinned: (pinned) => {
    writePinnedToStorage(PIN_KEY_DIAGRAMS, pinned);
    set((s) => ({
      diagramsPanelPinned: pinned,
      diagramsPanelOpen: pinned ? true : s.diagramsPanelOpen,
    }));
  },
  setRelationsPanelOpen: (open) => set({ relationsPanelOpen: open }),
  setRelationsPanelPinned: (pinned) => {
    writePinnedToStorage(PIN_KEY_RELATIONS, pinned);
    set((s) => ({
      relationsPanelPinned: pinned,
      relationsPanelOpen: pinned ? true : s.relationsPanelOpen,
    }));
  },
  setRelationsDirection: (direction) => {
    writeRelationsDirection(direction);
    set({ relationsDirection: direction });
  },
  setBaseFolderClassFilter: (filter) => set({ baseFolderClassFilter: filter }),
  setRevealMode: (mode) => {
    writeRevealMode(mode);
    set({ revealMode: mode });
  },
  setRevealPreviewOpen: (open) => {
    writeRevealPreviewOpen(open);
    set({ revealPreviewOpen: open });
  },
  setRevealPaneVariant: (variant) => set({ revealPaneVariant: variant }),
  setPlantumlLineType: (lineType) => {
    writePlantumlLineType(lineType);
    set({ plantumlLineType: lineType });
  },
  setPlantumlPerInstance: (perInstance) => {
    writePlantumlPerInstance(perInstance);
    set({ plantumlPerInstance: perInstance });
  },
  setSelectedConceptIds: (ids) => set({ selectedConceptIds: ids }),
  setSelectionAnchorConceptId: (id) => set({ selectionAnchorConceptId: id }),
  setSelectedRelationIds: (ids) => set({ selectedRelationIds: ids }),
  setSelectedEdgeIds: (ids) => set({ selectedEdgeIds: ids }),
  viewportFocusRequest: null,
  requestViewportFocus: (id) =>
    set((s) => ({
      viewportFocusRequest: { id, nonce: (s.viewportFocusRequest?.nonce ?? 0) + 1 },
    })),
  armFormatPainter: (captured) => set({ formatPainter: { captured } }),
  clearFormatPainter: () => set({ formatPainter: null }),
  setTextPaneLang: (lang) => set({ textPaneLang: lang }),

  promptNestRelationType: (sourceConceptId, targetConceptId, relationTypeIds, oldNesting) =>
    new Promise<{ chosen: string | null; removeOld: boolean }>((resolve) => {
      set({
        nestingPrompt: {
          kind: "pick-relation-type",
          sourceConceptId,
          targetConceptId,
          relationTypeIds,
          ...(oldNesting && oldNesting.relationIds.length > 0 ? { oldNesting } : {}),
          resolve: (chosen, removeOld) => resolve({ chosen, removeOld }),
        },
      });
    }),

  promptHiddenRelationPick: (relationIds) =>
    new Promise<string | null>((resolve) => {
      set({
        nestingPrompt: {
          kind: "pick-hidden-relation",
          relationIds,
          resolve,
        },
      });
    }),

  promptExistingOrNewRelation: (sourceConceptId, targetConceptId, existingRelationIds) =>
    new Promise<string | "new" | null>((resolve) => {
      set({
        nestingPrompt: {
          kind: "pick-existing-or-new",
          sourceConceptId,
          targetConceptId,
          existingRelationIds,
          resolve,
        },
      });
    }),

  promptMergeRelations: (subjectRelationId, candidateRelationIds) =>
    new Promise<string | "keep" | null>((resolve) => {
      set({
        nestingPrompt: {
          kind: "merge-relation",
          subjectRelationId,
          candidateRelationIds,
          resolve,
        },
      });
    }),

  promptMergeConcepts: (subjectConceptId, candidateConceptIds) =>
    new Promise<string | "keep" | null>((resolve) => {
      set({
        nestingPrompt: {
          kind: "merge-concept",
          subjectConceptId,
          candidateConceptIds,
          resolve,
        },
      });
    }),

  promptGroupOnEnclose: (groepNaam, items) =>
    new Promise<Record<string, string | null> | null>((resolve) => {
      set({ groupEnclosePrompt: { groepNaam, items, resolve } });
    }),

  resolveGroupEnclosePrompt: (keuze) => {
    const cur = useUi.getState().groupEnclosePrompt;
    if (cur) cur.resolve(keuze);
    set({ groupEnclosePrompt: null });
  },

  resolveNestingPrompt: (chosen, removeOld = false) => {
    const cur = useUi.getState().nestingPrompt;
    if (cur?.kind === "pick-relation-type") cur.resolve(chosen, removeOld);
    else if (cur) cur.resolve(chosen as never);
    set({ nestingPrompt: null });
  },
  setParentDeletePrompts: (prompts) => set({ parentDeletePrompts: prompts }),
  openRoadmapMatrixSetup: (diagramId) => set({ roadmapMatrixSetupDiagramId: diagramId }),
  closeRoadmapMatrixSetup: () => set({ roadmapMatrixSetupDiagramId: null }),
  setHomonymPrompts: (prompts) => set({ homonymPrompts: prompts }),
}));
